import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, IconButton, Paper, Slide, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import {
    REEL_MAX_DURATION_MS,
    REEL_MIN_CLIP_MS,
    REEL_MIN_DURATION_MS,
    ReelElement,
    ReelElementType,
    formatMs,
} from './reelCanvas.types';
import { capturePointer } from './pointerCapture';

export const REEL_TIMELINE_HEIGHT = 240;
export const REEL_TIMELINE_TOGGLE_HEIGHT = 30;

const LABEL_COL_WIDTH = 160;
const ROW_HEIGHT = 34;
const RULER_HEIGHT = 26;

export interface ReelTimelineProps {
    open: boolean;
    onToggle: () => void;
    /** Bottom → top z-order; rows display top-most first (matches Layers). */
    elements: ReelElement[];
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    durationMs: number;
    onDurationChange: (ms: number) => void;
    playheadMs: number;
    onSeek: (ms: number) => void;
    playing: boolean;
    onTogglePlay: () => void;
    /** Live clip edits while dragging. */
    onClipChange: (id: string, startMs: number, endMs: number) => void;
    /** Pointer released — persist / mark dirty. */
    onClipCommit?: (id: string) => void;
    /** Click on a clip / its label (without dragging) → open the component's settings. */
    onOpenElement?: (id: string) => void;
    readOnly?: boolean;
    /** Slide container (the stage). */
    containerRef: React.RefObject<HTMLElement | null>;
}

type ClipSession = {
    id: string;
    mode: 'move' | 'start' | 'end';
    startX: number;
    baseStart: number;
    baseEnd: number;
    moved: boolean;
};

const TYPE_ICON: Record<ReelElementType, React.ReactNode> = {
    media: <PermMediaOutlinedIcon fontSize="inherit" />,
    text: <TextFieldsIcon fontSize="inherit" />,
    container: <CropSquareIcon fontSize="inherit" />,
    audio: <AudiotrackIcon fontSize="inherit" />,
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Picks a tick spacing so the ruler stays readable at any duration. */
const pickTickMs = (durationMs: number, trackWidth: number): number => {
    const candidates = [250, 500, 1000, 2000, 5000, 10_000, 15_000, 30_000, 60_000, 120_000, 300_000, 600_000];
    const minPxPerTick = 64;
    for (const c of candidates) {
        if ((c / durationMs) * trackWidth >= minPxPerTick) return c;
    }
    return candidates[candidates.length - 1];
};

/**
 * Bottom-docked timeline. Collapsed to a slim toggle bar by default; slides up to expose the reel
 * duration control, a ruler with a scrubbable playhead, and one track per layer whose clip bar can
 * be moved (drag body) or trimmed (drag either edge).
 */
export const ReelTimeline: React.FC<ReelTimelineProps> = ({
    open,
    onToggle,
    elements,
    selectedId,
    onSelect,
    durationMs,
    onDurationChange,
    playheadMs,
    onSeek,
    playing,
    onTogglePlay,
    onClipChange,
    onClipCommit,
    onOpenElement,
    readOnly = false,
    containerRef,
}) => {
    const theme = useTheme();
    const trackAreaRef = useRef<HTMLDivElement | null>(null);
    const [trackWidth, setTrackWidth] = useState(0);
    const clipSessionRef = useRef<ClipSession | null>(null);
    const scrubbingRef = useRef(false);
    const [durationDraft, setDurationDraft] = useState<string>(String(durationMs / 1000));

    useEffect(() => {
        setDurationDraft(String(Math.round(durationMs / 100) / 10));
    }, [durationMs]);

    useLayoutEffect(() => {
        const el = trackAreaRef.current;
        if (!el) return;
        const update = () => setTrackWidth(el.clientWidth);
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [open]);

    const msToX = useCallback((ms: number) => (durationMs > 0 ? (ms / durationMs) * trackWidth : 0), [durationMs, trackWidth]);
    const xToMs = useCallback(
        (x: number) => (trackWidth > 0 ? clamp((x / trackWidth) * durationMs, 0, durationMs) : 0),
        [durationMs, trackWidth],
    );

    const ticks = useMemo(() => {
        if (trackWidth <= 0) return [] as number[];
        const step = pickTickMs(durationMs, trackWidth);
        const out: number[] = [];
        for (let t = 0; t <= durationMs; t += step) out.push(t);
        return out;
    }, [durationMs, trackWidth]);

    const commitDuration = () => {
        const seconds = Number(durationDraft.replace(',', '.'));
        if (!Number.isFinite(seconds)) {
            setDurationDraft(String(durationMs / 1000));
            return;
        }
        const ms = clamp(Math.round(seconds * 1000), REEL_MIN_DURATION_MS, REEL_MAX_DURATION_MS);
        if (ms !== durationMs) onDurationChange(ms);
        setDurationDraft(String(ms / 1000));
    };

    // ---- scrubbing on the ruler / track background ----
    const trackLocalX = (clientX: number) => {
        const rect = trackAreaRef.current?.getBoundingClientRect();
        return rect ? clientX - rect.left : 0;
    };
    const onRulerPointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        scrubbingRef.current = true;
        capturePointer(e);
        onSeek(xToMs(trackLocalX(e.clientX)));
    };
    const onRulerPointerMove = (e: React.PointerEvent) => {
        if (!scrubbingRef.current) return;
        onSeek(xToMs(trackLocalX(e.clientX)));
    };
    const onRulerPointerUp = (e: React.PointerEvent) => {
        scrubbingRef.current = false;
        try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    };

    // ---- clip drag / trim ----
    const beginClip = (e: React.PointerEvent, el: ReelElement, mode: ClipSession['mode']) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(el.id);
        clipSessionRef.current = { id: el.id, mode, startX: e.clientX, baseStart: el.startMs, baseEnd: el.endMs, moved: false };
        if (readOnly || el.locked) return;
        capturePointer(e);
    };
    const onClipPointerMove = (e: React.PointerEvent) => {
        const s = clipSessionRef.current;
        if (!s || trackWidth <= 0) return;
        if (!s.moved && Math.abs(e.clientX - s.startX) < 4) return;
        s.moved = true;
        const el = elements.find((x) => x.id === s.id);
        if (readOnly || el?.locked) return;
        const dMs = ((e.clientX - s.startX) / trackWidth) * durationMs;
        const len = s.baseEnd - s.baseStart;
        if (s.mode === 'move') {
            const start = clamp(s.baseStart + dMs, 0, durationMs - len);
            onClipChange(s.id, Math.round(start), Math.round(start + len));
        } else if (s.mode === 'start') {
            const start = clamp(s.baseStart + dMs, 0, s.baseEnd - REEL_MIN_CLIP_MS);
            onClipChange(s.id, Math.round(start), s.baseEnd);
        } else {
            const end = clamp(s.baseEnd + dMs, s.baseStart + REEL_MIN_CLIP_MS, durationMs);
            onClipChange(s.id, s.baseStart, Math.round(end));
        }
    };
    const endClip = (e: React.PointerEvent) => {
        const s = clipSessionRef.current;
        if (!s) return;
        clipSessionRef.current = null;
        try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
        const cur = elements.find((el) => el.id === s.id);
        if (cur && (cur.startMs !== s.baseStart || cur.endMs !== s.baseEnd)) {
            onClipCommit?.(s.id);
        } else if (!s.moved && s.mode === 'move') {
            onOpenElement?.(s.id);
        }
    };

    const rows = [...elements].reverse();
    const playheadX = msToX(playheadMs);
    const accent = theme.palette.secondary.main;
    const rulerBg = theme.palette.mode === 'dark' ? alpha('#000', 0.35) : alpha('#000', 0.05);

    return (
        <>
            {/* Collapsed toggle tab — always visible at the bottom centre */}
            {!open && (
                <Paper
                    elevation={4}
                    onPointerDown={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 0,
                        transform: 'translateX(-50%)',
                        zIndex: 25,
                        height: REEL_TIMELINE_TOGGLE_HEIGHT,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        borderRadius: '10px 10px 0 0',
                        border: `1px solid ${theme.palette.divider}`,
                        borderBottom: 'none',
                        cursor: 'pointer',
                        backgroundColor: alpha(theme.palette.background.paper, 0.96),
                        '&:hover': { backgroundColor: alpha(accent, 0.16) },
                    }}
                    onClick={onToggle}
                    role="button"
                    aria-label="Show timeline"
                >
                    <ExpandLessIcon fontSize="small" />
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                        TIMELINE · {formatMs(playheadMs)} / {formatMs(durationMs)}
                    </Typography>
                </Paper>
            )}

            <Slide in={open} direction="up" container={containerRef.current} mountOnEnter unmountOnExit appear>
                <Paper
                    square
                    elevation={8}
                    aria-label="Timeline"
                    onPointerDown={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: REEL_TIMELINE_HEIGHT,
                        zIndex: 25,
                        display: 'flex',
                        flexDirection: 'column',
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.background.paper, 0.98),
                        backdropFilter: 'blur(6px)',
                        userSelect: 'none',
                    }}
                >
                    {/* Transport row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5 }}>
                        <Tooltip title="Go to start">
                            <IconButton size="small" aria-label="Go to start" onClick={() => onSeek(0)}>
                                <SkipPreviousIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={playing ? 'Pause' : 'Play'}>
                            <IconButton
                                size="small"
                                aria-label={playing ? 'Pause' : 'Play'}
                                onClick={onTogglePlay}
                                sx={{ color: accent, border: `1px solid ${alpha(accent, 0.5)}` }}
                            >
                                {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 150 }}>
                            {formatMs(playheadMs)} / {formatMs(durationMs)}
                        </Typography>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">
                            Reel length
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            value={durationDraft}
                            disabled={readOnly}
                            onChange={(e) => setDurationDraft(e.target.value)}
                            onBlur={commitDuration}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                            }}
                            inputProps={{
                                min: REEL_MIN_DURATION_MS / 1000,
                                max: REEL_MAX_DURATION_MS / 1000,
                                step: 0.5,
                                'aria-label': 'Reel length in seconds',
                                style: { width: 72, textAlign: 'right' },
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            sec
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Hide timeline">
                            <IconButton size="small" aria-label="Hide timeline" onClick={onToggle}>
                                <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Divider />

                    {/* Ruler + tracks */}
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', height: RULER_HEIGHT, flexShrink: 0, backgroundColor: rulerBg }}>
                            <Box
                                sx={{
                                    width: LABEL_COL_WIDTH,
                                    flexShrink: 0,
                                    px: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRight: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Layers ({elements.length})
                                </Typography>
                            </Box>
                            <Box
                                ref={trackAreaRef}
                                onPointerDown={onRulerPointerDown}
                                onPointerMove={onRulerPointerMove}
                                onPointerUp={onRulerPointerUp}
                                onPointerCancel={onRulerPointerUp}
                                aria-label="Timeline ruler"
                                sx={{ position: 'relative', flex: 1, cursor: 'col-resize', touchAction: 'none', overflow: 'hidden' }}
                            >
                                {ticks.map((t) => (
                                    <Box key={t} sx={{ position: 'absolute', left: msToX(t), top: 0, bottom: 0, pointerEvents: 'none' }}>
                                        <Box sx={{ width: 1, height: 8, backgroundColor: alpha(theme.palette.text.primary, 0.4) }} />
                                        <Typography
                                            variant="caption"
                                            sx={{ position: 'absolute', top: 7, left: 3, fontSize: '0.62rem', color: 'text.secondary', whiteSpace: 'nowrap' }}
                                        >
                                            {formatMs(t).replace(/\.\d$/, '')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
                            {rows.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                    Add media, text, a container or audio from the SideKick menu — each layer gets its own track here.
                                </Typography>
                            )}
                            {rows.map((el) => {
                                const isSelected = el.id === selectedId;
                                const left = msToX(el.startMs);
                                const width = Math.max(4, msToX(el.endMs) - left);
                                const clipColor =
                                    el.type === 'media'
                                        ? theme.palette.info.main
                                        : el.type === 'text'
                                          ? theme.palette.success.main
                                          : el.type === 'audio'
                                            ? theme.palette.warning.main
                                            : accent;
                                return (
                                    <Box
                                        key={el.id}
                                        sx={{
                                            display: 'flex',
                                            height: ROW_HEIGHT,
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                            backgroundColor: isSelected ? alpha(accent, 0.08) : 'transparent',
                                            opacity: el.visible ? 1 : 0.5,
                                        }}
                                    >
                                        <Box
                                            onClick={() => {
                                                onSelect(el.id);
                                                onOpenElement?.(el.id);
                                            }}
                                            title="Open settings"
                                            sx={{
                                                width: LABEL_COL_WIDTH,
                                                flexShrink: 0,
                                                px: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                cursor: 'pointer',
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', fontSize: 16, color: 'text.secondary' }}>{TYPE_ICON[el.type]}</Box>
                                            <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 700 : 500 }} title={el.name}>
                                                {el.name}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{ position: 'relative', flex: 1, minWidth: 0 }}
                                            onPointerDown={(e) => {
                                                // clicking empty track space scrubs
                                                onSeek(xToMs(e.clientX - (e.currentTarget as HTMLElement).getBoundingClientRect().left));
                                            }}
                                        >
                                            <Box
                                                role="slider"
                                                aria-label={`${el.name} clip ${formatMs(el.startMs)} to ${formatMs(el.endMs)}`}
                                                aria-valuemin={0}
                                                aria-valuemax={durationMs}
                                                aria-valuenow={el.startMs}
                                                onPointerDown={(e) => beginClip(e, el, 'move')}
                                                onPointerMove={onClipPointerMove}
                                                onPointerUp={endClip}
                                                onPointerCancel={endClip}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 5,
                                                    height: ROW_HEIGHT - 10,
                                                    left,
                                                    width,
                                                    borderRadius: 1,
                                                    backgroundColor: alpha(clipColor, isSelected ? 0.85 : 0.6),
                                                    border: `1px solid ${alpha(clipColor, 1)}`,
                                                    boxShadow: isSelected ? `0 0 0 2px ${alpha(accent, 0.5)}` : 'none',
                                                    cursor: readOnly || el.locked ? 'default' : 'grab',
                                                    touchAction: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    overflow: 'hidden',
                                                    '&:active': { cursor: readOnly || el.locked ? 'default' : 'grabbing' },
                                                }}
                                            >
                                                <Box
                                                    aria-label="Trim start"
                                                    onPointerDown={(e) => beginClip(e, el, 'start')}
                                                    onPointerMove={onClipPointerMove}
                                                    onPointerUp={endClip}
                                                    onPointerCancel={endClip}
                                                    sx={{ width: 8, alignSelf: 'stretch', cursor: 'ew-resize', backgroundColor: alpha('#000', 0.25), flexShrink: 0 }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    noWrap
                                                    sx={{ flex: 1, px: 0.5, fontSize: '0.65rem', color: theme.palette.getContrastText(clipColor), pointerEvents: 'none' }}
                                                >
                                                    {formatMs(el.startMs)} – {formatMs(el.endMs)}
                                                </Typography>
                                                <Box
                                                    aria-label="Trim end"
                                                    onPointerDown={(e) => beginClip(e, el, 'end')}
                                                    onPointerMove={onClipPointerMove}
                                                    onPointerUp={endClip}
                                                    onPointerCancel={endClip}
                                                    sx={{ width: 8, alignSelf: 'stretch', cursor: 'ew-resize', backgroundColor: alpha('#000', 0.25), flexShrink: 0 }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                            {/* Playhead over the tracks */}
                            {trackWidth > 0 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        bottom: 0,
                                        left: LABEL_COL_WIDTH + playheadX,
                                        width: 2,
                                        backgroundColor: theme.palette.error.main,
                                        pointerEvents: 'none',
                                        zIndex: 3,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                    {/* Playhead marker on the ruler */}
                    {trackWidth > 0 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 41 + RULER_HEIGHT - 8,
                                left: LABEL_COL_WIDTH + playheadX - 6,
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: `8px solid ${theme.palette.error.main}`,
                                pointerEvents: 'none',
                                zIndex: 4,
                            }}
                        />
                    )}
                </Paper>
            </Slide>
        </>
    );
};

export default ReelTimeline;
